// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

export interface ProctoringConfig {
  sessionId: string;
  enabled: boolean;
  faceDetectionInterval?: number; // milliseconds (default: 10000 = 10 seconds)
  onViolation?: (violation: ProctoringViolation) => void;
}

export interface ProctoringViolation {
  type: 'face_count_zero' | 'face_count_multiple' | 'tab_switch';
  severity: 'info' | 'warning' | 'critical';
  message: string;
  timestamp: string;
}

/**
 * Hook for interview proctoring
 * - Detects faces in video stream
 * - Detects tab/screen sharing
 * - Reports violations to backend
 */
export function useProctoring(
  videoElement: HTMLVideoElement | null,
  config: ProctoringConfig
): {
  detectFace: () => Promise<void>;
  checkTabSwitch: () => Promise<void>;
  captureFrame: () => string | null;
} {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const detectionIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastViolationRef = useRef<{ type: string; time: number } | null>(null);

  /**
   * Capture current video frame as base64 image
   */
  const captureFrame = useCallback((): string | null => {
    if (!videoElement || videoElement.readyState < 2) {
      return null;
    }

    try {
      // Create canvas if not exists
      if (!canvasRef.current) {
        canvasRef.current = document.createElement('canvas');
      }

      const canvas = canvasRef.current;
      const video = videoElement;

      // Set canvas size to match video
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;

      // Draw video frame to canvas
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert to base64 JPEG (smaller than PNG)
      return canvas.toDataURL('image/jpeg', 0.8);
    } catch (error) {
      console.error('[Proctoring] Frame capture error:', error);
      return null;
    }
  }, [videoElement]);

  /**
   * Perform face detection on current frame
   */
  const detectFace = useCallback(async () => {
    if (!config.enabled) return;

    const frame = captureFrame();
    if (!frame) {
      console.log('[Proctoring] No frame captured, skipping detection');
      return;
    }

    try {
      const response = await axios.post('/api/proctoring/detect-face', {
        sessionId: config.sessionId,
        frame,
        timestamp: new Date().toISOString()
      });

      const { violation, faceCount, message, severity } = response.data;

      if (violation) {
        console.warn('[Proctoring] Violation detected:', message);

        const violationType = faceCount === 0 ? 'face_count_zero' : 'face_count_multiple';

        // Debounce violations - don't report same type within 30 seconds
        const now = Date.now();
        const lastViolation = lastViolationRef.current;
        if (lastViolation && lastViolation.type === violationType && now - lastViolation.time < 30000) {
          console.log('[Proctoring] Violation debounced (same type within 30s)');
          return;
        }

        lastViolationRef.current = { type: violationType, time: now };

        if (config.onViolation) {
          config.onViolation({
            type: violationType,
            severity,
            message,
            timestamp: new Date().toISOString()
          });
        }
      } else {
        console.log('[Proctoring] Face detection OK:', message);
      }
    } catch (error) {
      console.error('[Proctoring] Face detection API error:', error);
    }
  }, [config, captureFrame]);

  /**
   * Check if user switches away from interview tab
   */
  const checkTabSwitch = useCallback(async () => {
    if (!config.enabled) return;

    // This is now handled by the visibilitychange event listener
    // See useEffect below
  }, [config.enabled]);

  /**
   * Detect tab switches (when user leaves the interview tab)
   */
  useEffect(() => {
    if (!config.enabled) return;

    const handleVisibilityChange = async (): Promise<void> => {
      if (document.hidden) {
        // User switched away from this tab
        console.warn('[Proctoring] TAB SWITCH DETECTED - User left the interview tab');

        try {
          // Report to backend
          await axios.post('/api/proctoring/tab-switch', {
            sessionId: config.sessionId,
            details: {
              action: 'tab_hidden',
              timestamp: new Date().toISOString()
            },
            timestamp: new Date().toISOString()
          });

          if (config.onViolation) {
            config.onViolation({
              type: 'tab_switch',
              severity: 'critical',
              message: 'Candidate switched away from interview tab',
              timestamp: new Date().toISOString()
            });
          }
        } catch (error) {
          console.error('[Proctoring] Error reporting tab switch:', error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [config]);

  /**
   * Start face detection
   */
  useEffect(() => {
    if (!config.enabled || !videoElement) {
      return;
    }

    console.log('[Proctoring] Starting face detection for session:', config.sessionId);

    // Start periodic face detection
    const interval = config.faceDetectionInterval || 10000; // Default 10 seconds
    detectionIntervalRef.current = setInterval(() => {
      detectFace();
    }, interval);

    return () => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
        detectionIntervalRef.current = null;
      }
    };
  }, [config.enabled, config.sessionId, videoElement, detectFace, config.faceDetectionInterval]);

  return {
    detectFace,
    checkTabSwitch,
    captureFrame
  };
}
